<template>
  <v-container>
    <h1 class="text-center">Instagram-Ankündigungen</h1>
    <v-row>
      <v-col cols="12">
        <v-btn text small @click="showHowTo = !showHowTo">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ showHowTo ? 'Anleitung ausblenden' : 'Wie bekomme ich Business Account ID & Access Token?' }}
        </v-btn>
        <v-alert v-if="showHowTo" type="info" outlined dense class="mt-2">
          <ol class="pl-4">
            <li>Instagram-Konto in ein <b>Business- oder Creator-Konto</b> umwandeln (Instagram-App &rarr; Einstellungen &rarr; Konto &rarr; Zu professionellem Konto wechseln).</li>
            <li>Das Instagram-Konto mit einer <b>Facebook-Seite</b> verknüpfen (Instagram-App &rarr; Einstellungen &rarr; Konto &rarr; Verknüpfte Konten, oder über die Facebook-Seiteneinstellungen).</li>
            <li>Auf <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener">developers.facebook.com/apps</a> eine neue App anlegen (Typ "Business") und die Produkte <b>"Instagram Graph API"</b> sowie <b>"Facebook Login"</b> hinzufügen.</li>
            <li>Im <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noopener">Graph API Explorer</a> die eigene App auswählen und einen User-Access-Token mit den Berechtigungen <code>instagram_basic</code>, <code>pages_show_list</code> und <code>pages_read_engagement</code> generieren.</li>
            <li>Diesen Token über den <a href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noopener">Access Token Debugger</a> oder den <code>/oauth/access_token</code>-Endpoint in einen <b>langlebigen Token</b> (60 Tage gültig) umwandeln.</li>
            <li>Mit diesem Token <code>GET /me/accounts</code> aufrufen, um die eigene Facebook-Seiten-ID zu finden.</li>
            <li>Damit <code>GET /&#123;page-id&#125;?fields=instagram_business_account</code> aufrufen &rarr; das Ergebnis enthält die <b>Instagram Business Account ID</b>, die hier eingetragen wird.</li>
            <li>Für die automatische Token-Erneuerung zusätzlich <b>App ID</b> und <b>App Secret</b> deiner Meta-App eintragen (zu finden unter App-Einstellungen &rarr; Grundlegendes auf developers.facebook.com).</li>
          </ol>
          <div class="mt-2">
            <b>Hinweis:</b> Wenn App ID und App Secret hinterlegt sind, wird der Access Token automatisch alle ca. 45 Tage
            erneuert, bevor er nach 60 Tagen abläuft. Ohne diese Angaben läuft der Token nach ca. 60 Tagen ab und muss
            manuell erneuert werden.
          </div>
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-switch
          v-model="enabled"
          label="Ankündigung aktiviert"
        ></v-switch>
      </v-col>
      <v-col cols="12" md="4">
        <v-switch
          v-model="announcePosts"
          label="Beiträge ankündigen"
        ></v-switch>
      </v-col>
      <v-col cols="12" md="4">
        <v-switch
          v-model="announceReels"
          label="Reels ankündigen"
        ></v-switch>
      </v-col>
      <v-col cols="12" md="4">
        <v-switch
          v-model="announceStories"
          label="Stories ankündigen"
          hint="Story-Unterstützung ist durch die Instagram-API eingeschränkter als bei Beiträgen/Reels."
          persistent-hint
        ></v-switch>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="Instagram-Benutzername"
          v-model="instagramUsername"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="Instagram Business Account ID"
          hint="Die ID des mit einer Facebook-Seite verknüpften Instagram Business/Creator-Kontos."
          persistent-hint
          v-model="businessAccountId"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="Access Token"
          type="password"
          :placeholder="hasAccessToken ? 'Token gespeichert - zum Ändern neu eingeben' : 'Access Token eingeben'"
          hint="Wird verschlüsselt gespeichert und nie wieder angezeigt."
          persistent-hint
          v-model="accessToken"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="Meta App ID"
          hint="App ID deiner eigenen Meta-Developer-App (siehe Anleitung oben)."
          persistent-hint
          v-model="metaAppId"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          label="Meta App Secret"
          type="password"
          :placeholder="hasAppSecret ? 'Secret gespeichert - zum Ändern neu eingeben' : 'App Secret eingeben'"
          hint="Wird verschlüsselt gespeichert und nie wieder angezeigt. Wird zur automatischen Token-Erneuerung benötigt."
          persistent-hint
          v-model="metaAppSecret"
        ></v-text-field>
      </v-col>
      <v-col cols="12" v-if="hasAccessToken">
        <v-alert type="success" outlined dense v-if="metaAppId && hasAppSecret">
          Automatische Token-Erneuerung aktiv{{ tokenUpdatedAtLabel ? ` - zuletzt erneuert am ${tokenUpdatedAtLabel}` : '' }}. Der Token wird ca. alle 45 Tage automatisch verlängert.
        </v-alert>
        <v-alert type="warning" outlined dense v-else>
          Ohne Meta App ID + App Secret kann der Token nicht automatisch erneuert werden und läuft nach ca. 60 Tagen ab (zuletzt gesetzt{{ tokenUpdatedAtLabel ? ` am ${tokenUpdatedAtLabel}` : '' }}).
        </v-alert>
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          :items="channels"
          item-text="name"
          item-value="id"
          label="Discord-Kanal"
          v-model="channelId"
        ></v-select>
      </v-col>
      <v-col cols="12">
        <v-textarea
          label="Nachricht"
          hint="Platzhalter: {type}, {caption}, {url}, {username}."
          persistent-hint
          v-model="message"
        ></v-textarea>
      </v-col>
      <v-col cols="12">
        <v-btn color="primary" :block="$vuetify.breakpoint.smAndDown" @click="saveValues">Speichern</v-btn>
        <v-btn
          :class="$vuetify.breakpoint.smAndDown ? 'mt-2' : 'ml-2'"
          :block="$vuetify.breakpoint.smAndDown"
          outlined
          :disabled="!channelId"
          :loading="sendingTest"
          @click="sendTest"
        >
          Testnachricht senden
        </v-btn>
      </v-col>
    </v-row>
    <v-snackbar
      v-model="settingsSaved"
      timeout="3000"
      color="green"
    >
      Einstellungen gespeichert!
    </v-snackbar>
    <v-snackbar
      v-model="testResult.show"
      timeout="5000"
      :color="testResult.success ? 'green' : 'red'"
    >
      {{ testResult.message }}
    </v-snackbar>
  </v-container>
</template>

<script>

import BackendService from '@/services/BackendService';

export default {
  data() {
    return {
      showHowTo: false,
      settingsSaved: false,
      enabled: false,
      announcePosts: true,
      announceReels: true,
      announceStories: false,
      instagramUsername: '',
      businessAccountId: '',
      accessToken: '',
      hasAccessToken: false,
      metaAppId: '',
      metaAppSecret: '',
      hasAppSecret: false,
      tokenUpdatedAt: null,
      channelId: null,
      message: '',
      channels: [],
      sendingTest: false,
      testResult: {
        show: false,
        success: false,
        message: '',
      },
    };
  },
  computed: {
    tokenUpdatedAtLabel() {
      return this.tokenUpdatedAt ? new Date(this.tokenUpdatedAt).toLocaleDateString('de-DE') : '';
    },
  },
  mounted() {
    BackendService.loadInstagramAnnouncementSettings().then((data) => {
      const { settings } = data.data;
      this.enabled = Number(settings.instagram_announce_enabled) === 1;
      this.announcePosts = Number(settings.instagram_announce_posts) === 1;
      this.announceReels = Number(settings.instagram_announce_reels) === 1;
      this.announceStories = Number(settings.instagram_announce_stories) === 1;
      this.instagramUsername = settings.instagram_username || '';
      this.businessAccountId = settings.instagram_business_account_id || '';
      this.hasAccessToken = !!settings.hasAccessToken;
      this.metaAppId = settings.instagram_meta_app_id || '';
      this.hasAppSecret = !!settings.hasAppSecret;
      this.tokenUpdatedAt = settings.instagram_token_updated_at || null;
      this.channelId = settings.instagram_announce_channel_id;
      this.message = settings.instagram_announce_message || '';
      this.channels = data.data.channels;
    });
  },
  methods: {
    saveValues() {
      BackendService.saveInstagramAnnouncementSettings({
        instagram_announce_enabled: this.enabled ? 1 : 0,
        instagram_announce_posts: this.announcePosts ? 1 : 0,
        instagram_announce_reels: this.announceReels ? 1 : 0,
        instagram_announce_stories: this.announceStories ? 1 : 0,
        instagram_username: this.instagramUsername,
        instagram_business_account_id: this.businessAccountId,
        instagram_access_token: this.accessToken,
        instagram_meta_app_id: this.metaAppId,
        instagram_meta_app_secret: this.metaAppSecret,
        instagram_announce_channel_id: this.channelId,
        instagram_announce_message: this.message,
      }).then(() => {
        this.settingsSaved = true;
        if (this.accessToken) {
          this.hasAccessToken = true;
          this.accessToken = '';
          this.tokenUpdatedAt = new Date().toISOString();
        }
        if (this.metaAppSecret) {
          this.hasAppSecret = true;
          this.metaAppSecret = '';
        }
      }).catch(() => {
        alert('nö!');
      });
    },
    sendTest() {
      this.sendingTest = true;
      BackendService.sendTestInstagramAnnouncement({
        instagram_username: this.instagramUsername,
        instagram_announce_channel_id: this.channelId,
        instagram_announce_message: this.message,
      }).then((data) => {
        this.testResult = {
          show: true,
          success: data.data.success,
          message: data.data.success
            ? 'Testnachricht wurde gesendet! Schau in den Discord-Kanal.'
            : `Fehlgeschlagen: ${data.data.error}`,
        };
      }).catch(() => {
        this.testResult = {
          show: true,
          success: false,
          message: 'Fehlgeschlagen: Verbindung zum Server nicht möglich.',
        };
      }).finally(() => {
        this.sendingTest = false;
      });
    },
  },
};
</script>
